// Performance optimization utilities for better Core Web Vitals and SEO

/**
 * Lazy load images with Intersection Observer
 */
export function initLazyLoading() {
  const images = document.querySelectorAll("img[data-src]");

  const imageObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          img.src = img.dataset.src || "";
          img.removeAttribute("data-src");
          imageObserver.unobserve(img);
        }
      });
    },
    {
      rootMargin: "50px 0px",
      threshold: 0.01,
    }
  );

  images.forEach((img) => imageObserver.observe(img));
}

/**
 * Preload critical resources
 */
export function preloadCriticalResources() {
  // Skip font preloading if fonts don't exist yet
  // You can add actual font paths when you have them
  const fonts: string[] = [
    // '/fonts/inter-var.woff2',
    // '/fonts/inter-latin-ext.woff2'
  ];

  fonts.forEach((font) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.href = font;
    link.as = "font";
    link.type = "font/woff2";
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  });
}

/**
 * Optimize First Input Delay (FID)
 */
export function optimizeFID() {
  // Break up long tasks
  const yieldToMain = () => {
    return new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  };

  // Use this when processing heavy computations
  return yieldToMain;
}

/**
 * Monitor Core Web Vitals using native Performance Observer API
 */
export function reportWebVitals(
  onPerfEntry?: (metric: { name: string; value: number }) => void
) {
  if (!onPerfEntry || typeof onPerfEntry !== "function") return;

  // Check if Performance Observer is supported
  if (!("PerformanceObserver" in window)) {
    console.log("PerformanceObserver not supported");
    return;
  }

  try {
    // Observe Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        onPerfEntry({ name: "LCP", value: lastEntry.startTime });
      }
    });
    lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });

    // Observe First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if ("processingStart" in entry && "startTime" in entry) {
          const processingStart = (entry as any).processingStart;
          const value = processingStart - entry.startTime;
          onPerfEntry({ name: "FID", value });
        }
      });
    });
    fidObserver.observe({ entryTypes: ["first-input"] });

    // Observe Cumulative Layout Shift
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if ("hadRecentInput" in entry && !(entry as any).hadRecentInput) {
          clsValue += (entry as any).value || 0;
          onPerfEntry({ name: "CLS", value: clsValue });
        }
      });
    });
    clsObserver.observe({ entryTypes: ["layout-shift"] });

    // First Contentful Paint
    const paintObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name === "first-contentful-paint") {
          onPerfEntry({ name: "FCP", value: entry.startTime });
        }
      });
    });
    paintObserver.observe({ entryTypes: ["paint"] });

    // Time to First Byte
    const navigationObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const navEntry = entries[0] as PerformanceNavigationTiming;
      if (navEntry && navEntry.responseStart) {
        const ttfb = navEntry.responseStart - navEntry.fetchStart;
        onPerfEntry({ name: "TTFB", value: ttfb });
      }
    });
    navigationObserver.observe({ entryTypes: ["navigation"] });
  } catch (e) {
    console.log("Error setting up performance observers:", e);
  }
}

/**
 * Prefetch links on hover
 */
export function initPrefetching() {
  const prefetchedUrls = new Set<string>();

  const prefetchLink = (url: string) => {
    if (prefetchedUrls.has(url)) return;

    const link = document.createElement("link");
    link.rel = "prefetch";
    link.href = url;
    document.head.appendChild(link);
    prefetchedUrls.add(url);
  };

  document.addEventListener("mouseover", (e) => {
    const target = e.target as HTMLElement;
    const link = target.closest("a");

    if (link && link.href && link.hostname === window.location.hostname) {
      prefetchLink(link.href);
    }
  });
}

/**
 * Optimize Cumulative Layout Shift (CLS)
 */
export function reserveSpaceForImages() {
  const images = document.querySelectorAll("img:not([width]):not([height])");

  images.forEach((img) => {
    if (img instanceof HTMLImageElement && img.dataset.aspectRatio) {
      const [width, height] = img.dataset.aspectRatio.split(":").map(Number);
      if (width && height) {
        img.style.aspectRatio = `${width}/${height}`;
      }
    }
  });
}

/**
 * Initialize all performance optimizations
 */
export function initPerformanceOptimizations() {
  // Run immediately
  preloadCriticalResources();

  // Run after DOM is loaded
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initLazyLoading();
      initPrefetching();
      reserveSpaceForImages();
    });
  } else {
    initLazyLoading();
    initPrefetching();
    reserveSpaceForImages();
  }

  // Monitor performance with a simple console logger
  reportWebVitals((metric) => {
    console.log(`${metric.name}:`, metric.value.toFixed(2));
  });
}
